import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, switchMap } from 'rxjs';
import { shareReplay, take, tap } from 'rxjs/operators';
import { getLogger } from 'tofe-core';
import { LanguageConfigurationService } from 'tofe-language';

import { DefaultCustomization } from './customization.constants';
import { CustomizationLoaderService } from './customization-loader.service';
import { TofeCustomization } from './models/tofe-customization.model';

const logger = getLogger('[Service][Customization]');
const CUSTOMIZATION_FONT_LINK_CLASS = 'tofe-customization-font';
const CUSTOMIZATION_STYLESHEET_LINK_CLASS = 'tofe-customization-stylesheet';

@Injectable({
  providedIn: 'root',
})
export class CustomizationService {
  private readonly conf = inject(LanguageConfigurationService);
  private readonly document = inject(DOCUMENT);

  readonly current$: Observable<TofeCustomization>;
  private readonly currentSubject = new BehaviorSubject<string>('default');
  private current: TofeCustomization = DefaultCustomization;
  private originalFaviconHref: string | null = null;

  constructor() {
    const customizationLoader = inject(CustomizationLoaderService);

    this.current$ = this.currentSubject.asObservable().pipe(
      filter((x) => !!x),
      tap((y) => {
        logger.debug(`using ${y}`);
      }),
      switchMap((name) => customizationLoader.getCustomization(name)),
      tap({
        next: (y) => {
          logger.debug(`current$`, y);
          this.initCurrent(y);
        },
        error: (err) => {
          logger.error(`current$ error`, err);
        },
      }),
      shareReplay(1),
    );
  }

  setCurrent(name?: string): Observable<TofeCustomization> {
    logger.debug(`setCurrent`, name);
    this.currentSubject.next(name ?? 'default');
    return this.current$.pipe(take(1));
  }

  getCurrent(): TofeCustomization {
    return this.current;
  }

  /**
   * Whether the current customization hides the page footer. Defaults to `false` (footer shown)
   * when not specified.
   */
  isFooterHidden(): boolean {
    return !!this.getCurrent().hideFooter;
  }

  /**
   * Whether the current customization suppresses the final "completed" page, ending the process
   * with a direct redirect to `successRedirectUrl` instead. Defaults to `false` (completed page
   * shown) when not specified.
   */
  isCompletedStepDisabled(): boolean {
    return !!this.getCurrent().disableCompletedStep;
  }

  initCssVariables() {
    // Apply CSS variables to document root so they're inherited globally
    const style = this.document.documentElement.style;
    const cssVariables = this.getCurrent().cssVariables ?? {};
    Object.keys(cssVariables).forEach((key) => {
      style.setProperty(key, cssVariables[key]);
    });
  }

  private initCurrent(current: TofeCustomization) {
    this.current = current;
    const language = current.language;
    if (language) {
      this.conf.setCustomization(language);
    }
    this.loadCustomizationFonts(current);
    this.loadCustomizationStylesheets(current);
    this.applyCustomizationFavicon(current);
  }

  /**
   * Apply a custom favicon for the current customization. Updates the existing
   * `<link rel="icon">` (declared in index.html) and restores the original favicon when the
   * active customization doesn't override it.
   */
  private applyCustomizationFavicon(customization: TofeCustomization): void {
    const faviconUrl = customization.faviconUrl;

    let link = this.document.head.querySelector<HTMLLinkElement>("link[rel~='icon']");

    // Remember the original favicon once, so we can restore it when switching back.
    if (this.originalFaviconHref === null) {
      this.originalFaviconHref = link?.getAttribute('href') ?? '';
    }

    if (!faviconUrl) {
      if (link && this.originalFaviconHref) {
        link.href = this.originalFaviconHref;
      }
      return;
    }

    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.href = faviconUrl;
    logger.debug(`Favicon set for customization ${customization.name}: ${faviconUrl}`);
  }

  /**
   * Load font URLs dynamically for the current customization.
   * Removes previously loaded customization fonts and injects new <link> tags into the document head.
   */
  private loadCustomizationFonts(customization: TofeCustomization): void {
    // Remove any previously loaded customization fonts
    this.removeCustomizationFonts();

    const fontUrls = customization.fontUrls;

    if (!fontUrls || !Array.isArray(fontUrls) || fontUrls.length === 0) {
      logger.debug('No font URLs defined for customization', customization.name);
      return;
    }

    logger.debug(`Loading ${fontUrls.length} font(s) for customization`, customization.name);

    // Inject font link tags into document head
    fontUrls.forEach((url, index) => {
      const link = this.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.className = CUSTOMIZATION_FONT_LINK_CLASS;
      link.setAttribute('data-customization', customization.name);
      link.setAttribute('data-font-index', index.toString());

      this.document.head.appendChild(link);
      logger.debug(`Font loaded: ${url}`);
    });
  }

  /**
   * Remove all font link tags previously injected by customizations.
   */
  private removeCustomizationFonts(): void {
    const existingFontLinks = this.document.head.querySelectorAll(`link.${CUSTOMIZATION_FONT_LINK_CLASS}`);

    existingFontLinks.forEach((link) => {
      logger.debug('Removing font link:', link.getAttribute('href'));
      link.remove();
    });
  }

  /**
   * Load stylesheet URLs dynamically for the current customization.
   * Removes previously loaded customization stylesheets and injects new <link> tags into the document head.
   */
  private loadCustomizationStylesheets(customization: TofeCustomization): void {
    // Remove any previously loaded customization stylesheets
    this.removeCustomizationStylesheets();

    const styleUrls = customization.styleUrls;

    if (!styleUrls || !Array.isArray(styleUrls) || styleUrls.length === 0) {
      logger.debug('No stylesheet URLs defined for customization', customization.name);
      return;
    }

    logger.debug(`Loading ${styleUrls.length} stylesheet(s) for customization`, customization.name);

    // Inject stylesheet link tags into document head
    styleUrls.forEach((url, index) => {
      const link = this.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.className = CUSTOMIZATION_STYLESHEET_LINK_CLASS;
      link.setAttribute('data-customization', customization.name);
      link.setAttribute('data-style-index', index.toString());

      this.document.head.appendChild(link);
      logger.debug(`Stylesheet loaded: ${url}`);
    });
  }

  /**
   * Remove all stylesheet link tags previously injected by customizations.
   */
  private removeCustomizationStylesheets(): void {
    const existingStyleLinks = this.document.head.querySelectorAll(`link.${CUSTOMIZATION_STYLESHEET_LINK_CLASS}`);

    existingStyleLinks.forEach((link) => {
      logger.debug('Removing stylesheet link:', link.getAttribute('href'));
      link.remove();
    });
  }
}
